import { Bus, Tractor, HardHat, Car, Truck, Container, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/i18n/LanguageContext";
import { routes } from "@/i18n/routes";

import serviceBus from "@/assets/service-bus.jpg";
import serviceBusAvif from "@/assets/service-bus.avif";

import serviceAgricultural from "@/assets/service-agricultural.jpg";
import serviceAgriculturalAvif from "@/assets/service-agricultural.avif";

import serviceConstruction from "@/assets/service-construction.jpg";
import serviceConstructionAvif from "@/assets/service-construction.avif";

import serviceMercedes from "@/assets/service-mercedes.jpg";
import serviceMercedesAvif from "@/assets/service-mercedes.avif";

import serviceMan from "@/assets/service-man.jpg";
import serviceManAvif from "@/assets/service-man.avif";

import serviceTrailer from "@/assets/service-trailer.jpg";
import serviceTrailerAvif from "@/assets/service-trailer.avif";

const ServicesSection = () => {
  const { t, language } = useLanguage();

  const services = [
    {
      icon: Bus,
      title: t("service.bus.title"),
      description: t("service.bus.desc"),
      features: [t("service.bus.f1"), t("service.bus.f2"), t("service.bus.f3")],
      image: serviceBus,
      imageAvif: serviceBusAvif,
      href: `/${language}/${routes.services.bus[language]}`,
    },
    {
      icon: Tractor,
      title: t("service.agri.title"),
      description: t("service.agri.desc"),
      features: [t("service.agri.f1"), t("service.agri.f2"), t("service.agri.f3")],
      image: serviceAgricultural,
      imageAvif: serviceAgriculturalAvif,
      href: `/${language}/${routes.services.agricultural[language]}`,
    },
    {
      icon: HardHat,
      title: t("service.construction.title"),
      description: t("service.construction.desc"),
      features: [t("service.construction.f1"), t("service.construction.f2"), t("service.construction.f3")],
      image: serviceConstruction,
      imageAvif: serviceConstructionAvif,
      href: `/${language}/${routes.services.construction[language]}`,
    },
    {
      icon: Car,
      title: t("service.mercedes.title"),
      description: t("service.mercedes.desc"),
      features: ["Actros", "Atego", "Arocs"],
      image: serviceMercedes,
      imageAvif: serviceMercedesAvif,
      href: `/${language}/${routes.services.mercedes[language]}`,
    },
    {
      icon: Truck,
      title: t("service.man.title"),
      description: t("service.man.desc"),
      features: ["TGX", "TGS", "TGM"],
      image: serviceMan,
      imageAvif: serviceManAvif,
      href: `/${language}/${routes.services.man[language]}`,
    },
    {
      icon: Container,
      title: t("service.trailer.title"),
      description: t("service.trailer.desc"),
      features: [t("service.trailer.f1"), t("service.trailer.f2"), t("service.trailer.f3")],
      image: serviceTrailer,
      imageAvif: serviceTrailerAvif,
      href: `/${language}/${routes.services.trailer[language]}`,
    },
  ];

  return (
    <section id="services" className="py-24 bg-background">
      <div className="container mx-auto px-4">

        <div className="text-center max-w-4xl mx-auto mb-16">
          <span className="inline-block text-accent font-semibold mb-4 uppercase tracking-wider text-sm">
            {t("services.label")}
          </span>

          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            {t("services.title")}
          </h2>

          <p className="text-muted-foreground text-lg">
            {t("services.description")}
          </p>

          <p className="text-muted-foreground text-lg mt-4">
            {t("services.description2")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;

            return (
              <Card
                key={service.href}
                className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-accent/50 overflow-hidden"
              >
                <div className="relative h-48 overflow-hidden">

                  <picture>
                    <source srcSet={service.imageAvif} type="image/avif" />

                    <img
                      src={service.image}
                      alt={service.title}
                      loading={index === 0 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : undefined}
                      decoding="async"
                      width="400"
                      height="300"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </picture>

                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />

                  <div className="absolute bottom-4 left-4 w-12 h-12 rounded-lg bg-primary flex items-center justify-center group-hover:bg-accent transition-colors duration-300">
                    <Icon className="w-6 h-6 text-primary-foreground" />
                  </div>

                </div>

                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-bold">
                    {service.title}
                  </CardTitle>

                  <CardDescription className="text-muted-foreground">
                    {service.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    asChild
                    variant="ghost"
                    className="group/btn p-0 text-primary hover:bg-transparent hover:text-accent"
                  >
                    <a
                      href={service.href}
                      aria-label={`${t("services.learnMore")} - ${service.title}`}
                    >
                      {t("services.learnMore")}
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                    </a>
                  </Button>

                </CardContent>
              </Card>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default ServicesSection;